// Beat-Level Rewrite - Rewrite a specific beat/sequence
export async function requestBeatRewrite(beat, allScenes, frameworkType) {
  // Extract scenes for this beat based on page numbers
  const beatScenes = allScenes.filter(scene => {
    const scenePage = scene.pageEstimate || 0;
    return scenePage >= beat.startPage && scenePage <= beat.endPage;
  });

  if (beatScenes.length === 0) {
    throw new Error('No scenes found for this beat');
  }

  const beatName = beat.beat?.name || beat.sequence?.name || 'This Section';
  const beatDescription = beat.beat?.description || beat.sequence?.description || '';
  const issues = beat.notes || beat.microUnits?.action?.description || 'Structural improvements needed';

  const scenesText = beatScenes.map(s => {
    const header = s.header || 'UNTITLED SCENE';
    const text = Array.isArray(s.text) ? s.text.join('\n') : (s.text || '');
    return `${header}\n${text}`;
  }).join('\n\n');

  const prompt = `You are a professional screenplay doctor with expertise in ${frameworkType} structure. Rewrite this beat to address the identified issues while preserving the writer's voice.

BEAT: ${beatName}
Pages ${beat.startPage}-${beat.endPage}
Framework: ${frameworkType}

CURRENT HEALTH STATUS: ${beat.health}

STRUCTURAL GUIDANCE:
${beatDescription}

IDENTIFIED ISSUES:
${issues}

ORIGINAL SCENES:
${scenesText}

REWRITE INSTRUCTIONS:
1. Fix the structural issues identified in the ${frameworkType} analysis
2. Maintain character voices and relationships from the original
3. Keep key plot points, but improve their execution
4. Enhance pacing, stakes, and emotional impact
5. Stay within the page range (${beat.startPage}-${beat.endPage})
6. Use proper screenplay format (scene headings, action, dialogue)

Return ONLY the rewritten scenes in screenplay format. No explanations or notes.`;

  const response = await fetch('/api/analyze', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ prompt })
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.error || 'Beat rewrite request failed');
  }

  const data = await response.json();

  // Safely extract rewritten text
  if (!data.content || !data.content[0] || !data.content[0].text) {
    throw new Error('Invalid response from AI - missing rewritten text');
  }

  const rewrittenText = data.content[0].text;

  return {
    original: scenesText,
    rewritten: rewrittenText.trim(),
    beatName,
    pageRange: `${beat.startPage}-${beat.endPage}`,
    framework: frameworkType
  };
}

// Full Script Rewrite with Preservation Control
export async function requestFullRewrite(scriptText, analysis, frameworkAnalyses, preservationLevel, approvedBeatRewrites) {
  const preservationSettings = getPreservationSettings(preservationLevel);
  const approvedChanges = approvedBeatRewrites.map(br => `
Beat: ${br.beatName} (${br.pageRange})
Framework: ${br.framework}
Original: ${br.original.substring(0, 500)}...
Improved: ${br.rewritten.substring(0, 500)}...
`).join('\n\n');

  const allIssues = extractAllIssues(frameworkAnalyses);

  const prompt = `You are an expert screenplay doctor. Rewrite this screenplay to fix structural issues while respecting the writer's creative vision.

PRESERVATION LEVEL: ${preservationLevel}/100 - ${preservationSettings.description}

SCRIPT OVERVIEW:
Total Pages: ${analysis.totalPages || 0}
Total Scenes: ${analysis.totalScenes || 0}
Main Characters: ${analysis.characters ? analysis.characters.slice(0, 8).map(c => c.name).join(', ') : 'Unknown'}

FRAMEWORK ANALYSIS SUMMARY:
${allIssues}

${approvedBeatRewrites.length > 0 ? `
APPROVED BEAT REWRITES (Use these as examples of desired tone/style):
${approvedChanges}
` : ''}

REWRITE GUIDELINES:
${preservationSettings.guidelines}

ORIGINAL SCREENPLAY:
${scriptText.substring(0, 50000)}
${scriptText.length > 50000 ? '\n\n[Script continues...]' : ''}

INSTRUCTIONS:
1. Address all critical structural issues
2. ${preservationSettings.dialogueInstruction}
3. ${preservationSettings.sceneInstruction}
4. ${preservationSettings.characterInstruction}
5. ${preservationSettings.creativityInstruction}

Return the rewritten screenplay in proper format.`;

  const response = await fetch('/api/analyze', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ prompt })
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.error || 'Full rewrite request failed');
  }

  const data = await response.json();

  // Safely extract rewritten text
  if (!data.content || !data.content[0] || !data.content[0].text) {
    throw new Error('Invalid response from AI - missing rewritten screenplay');
  }

  return {
    rewritten: data.content[0].text.trim(),
    preservationLevel,
    settings: preservationSettings,
    issuesAddressed: allIssues
  };
}

function getPreservationSettings(level) {
  if (level >= 80) {
    return {
      description: 'Maximum Preservation - Minimal changes, only critical fixes',
      guidelines: `- Keep ALL existing dialogue verbatim\n- Only restructure scenes if absolutely necessary\n- Preserve original scene order\n- Fix only critical structural issues`,
      dialogueInstruction: 'Keep all dialogue exactly as written',
      sceneInstruction: 'Preserve original scene structure, only reorder if critical',
      characterInstruction: 'Maintain exact character voices and arcs',
      creativityInstruction: 'Make minimal changes - only fix what is broken'
    };
  } else if (level >= 60) {
    return {
      description: 'High Preservation - Keep voice and style, improve structure',
      guidelines: `- Preserve 80%+ of original dialogue\n- Maintain writer's unique voice\n- Restructure only when framework shows issues\n- Keep all major plot points`,
      dialogueInstruction: 'Keep most dialogue, polish only where needed',
      sceneInstruction: 'Preserve scene essence, improve structure and pacing',
      characterInstruction: 'Maintain character voices with subtle enhancements',
      creativityInstruction: 'Improve execution while keeping creative vision'
    };
  } else if (level >= 40) {
    return {
      description: 'Balanced - Fix structure, maintain core story',
      guidelines: `- Preserve core dialogue but refine for impact\n- Maintain story essence\n- Rewrite scenes as needed\n- Keep character arcs but enhance`,
      dialogueInstruction: 'Refine dialogue for clarity and impact',
      sceneInstruction: 'Restructure scenes to improve flow',
      characterInstruction: 'Maintain essence, enhance emotional depth',
      creativityInstruction: 'Balance vision with professional standards'
    };
  } else if (level >= 20) {
    return {
      description: 'Creative Enhancement - Significant improvements',
      guidelines: `- Rewrite dialogue for maximum impact\n- Major scene restructuring\n- Enhance character development\n- Keep premise, elevate execution`,
      dialogueInstruction: 'Rewrite dialogue for professional quality',
      sceneInstruction: 'Restructure extensively for optimal flow',
      characterInstruction: 'Deepen character arcs and motivations',
      creativityInstruction: 'Make bold improvements, keep core premise'
    };
  } else {
    return {
      description: 'Maximum Creativity - Fresh take on concept',
      guidelines: `- Complete creative reimagining\n- Professional-grade everything\n- Optimal framework alignment\n- Keep only core premise`,
      dialogueInstruction: 'Write entirely new professional dialogue',
      sceneInstruction: 'Create optimal structure from scratch',
      characterInstruction: 'Fully develop character arcs',
      creativityInstruction: 'Reimagine with professional standards'
    };
  }
}

function extractAllIssues(frameworkAnalyses) {
  if (!frameworkAnalyses || !frameworkAnalyses.analyses) return 'No issues identified';

  const issues = [];
  frameworkAnalyses.analyses.forEach(framework => {
    const fwType = framework.framework;
    if (framework.beats) {
      framework.beats.forEach(beat => {
        if (beat.health !== 'good') {
          const name = beat.beat?.name || beat.sequence?.name || 'Section';
          issues.push(`- [${fwType}] ${name} (pages ${beat.startPage}-${beat.endPage}): ${beat.health} health`);
        }
      });
    }
    if (framework.threads) {
      framework.threads.forEach(thread => {
        if (thread.health !== 'good') {
          issues.push(`- [${fwType}] ${thread.type} thread: ${thread.health}`);
        }
      });
    }
    if (framework.issues && framework.issues.length > 0) {
      framework.issues.forEach(issue => issues.push(`- [${fwType}] ${issue}`));
    }
  });
  return issues.length > 0 ? issues.join('\n') : 'Structure is solid';
}
